import { useContext } from "react"

import { graphql, useStaticQuery } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import { useI18next, useTranslation } from "gatsby-plugin-react-i18next"
import styled, { css } from "styled-components"

import InnerWrapper from "components/InnerWrapper"
import { ScreenContext } from "components/Providers"
import Callout from "components/TextElements/Callout"
import colors from "styles/colors"
import text from "styles/text"
import { fmobile, fresponsive, ftablet } from "utils/fullyResponsive"
import { checkedLocaleLink } from "utils/routes"
import useMedia from "utils/useMedia"

export default function Platform() {
  const { tablet } = useContext(ScreenContext)
  const { t } = useTranslation()
  const { language } = useI18next()
  const mainCardAltText = t("Card with Galileo logo and graphics on it")

  const homePlatform: Queries.HomePlatformQuery = useStaticQuery(graphql`
    query HomePlatform {
      card: file(relativePath: { eq: "homepage/cardIllustration.webp" }) {
        id
        childImageSharp {
          gatsbyImageData(width: 520, height: 820)
        }
      }
      cardM: file(
        relativePath: { eq: "homepage/cardIllustrationMobile.webp" }
      ) {
        id
        childImageSharp {
          gatsbyImageData(width: 335, height: 280)
        }
      }
    }
  `)

  const imageSrc = useMedia(
    homePlatform.card?.childImageSharp?.gatsbyImageData,
    homePlatform.card?.childImageSharp?.gatsbyImageData,
    homePlatform.card?.childImageSharp?.gatsbyImageData,
    homePlatform.cardM?.childImageSharp?.gatsbyImageData
  )

  return (
    <>
      <Wrapper>
        <InnerWrapper left={tablet}>
          <Content>
            {imageSrc && (
              <PlatformImage
                image={imageSrc}
                alt="Card with Galileo logo and graphics on it"
              />
            )}
            <Callout
              variant="darkMode"
              title={{
                content: t("One platform, endless solutions."),
                width: useMedia("811px", "48.855vw", undefined, "89.333vw"),
                size: useMedia(text.h2, text.h2, text.h4, text.h1Mobile),
              }}
              text={{
                content: t(
                  "Invent, build, launch and scale your own unique financial services with our proven platform that's secure and compliant from the ground up.  Create a custom, multi-product solution including card issuing, payments, checking, savings, deposits, lending and future products—all surfaced through industry-leading APIs."
                ),
                width: useMedia("627px", "37.771vw", undefined, undefined),
              }}
              kicker={{
                text:
                  t("Financial technology platform") ??
                  "Financial technology platform",
                icon: "cards",
              }}
              left={tablet}
              buttons={[
                {
                  text: t("Explore Our Platform") ?? "Explore Our Platform",
                  type: "secondary",
                  arrow: true,
                  route: checkedLocaleLink("/platform/galileo-open-apis", language),
                },
              ]}
            />
          </Content>
        </InnerWrapper>
      </Wrapper>
      <WrapperMobile>
        {imageSrc && (
          <MobileImage image={imageSrc} alt={mainCardAltText} loading="lazy" />
        )}
      </WrapperMobile>
    </>
  )
}

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  background: ${colors.darkMode};
  overflow: hidden;

  ${fresponsive(css`
    padding-bottom: 80px;
  `)}

  ${ftablet(css`
    padding-top: 198px;
    height: 614px;
  `)}

  ${fmobile(css`
    height: initial;
  `)}
`

const Content = styled.div`
  display: flex;
`

const PlatformImage = styled(GatsbyImage)`
  position: relative;

  ${fresponsive(css`
    width: 520px;
    height: 820px;
    margin-right: 150px;
  `)}

  ${ftablet(css`
    width: 260px;
    height: 410px;
    margin-right: 145px;
  `)}

  ${fmobile(css`
    display: none;
  `)}
`

const WrapperMobile = styled.div`
  width: 100%;
  display: none;
  justify-content: center;
  background: ${colors.darkMode};

  ${ftablet(css`
    margin-top: -2px;
  `)}

  ${fmobile(css`
    padding-top: 80px;
    display: flex;
  `)}
`

const MobileImage = styled(GatsbyImage)`
  height: auto;
  object-fit: contain;
  filter: drop-shadow(2.185vw 4.371vw 8.741vw rgba(1, 3, 13, 0.3));
  ${fmobile(css`
    width: 3365x;
  `)}
`
